import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";

export const CustomerTagsSelector = memo(function CustomerTagsSelector({
  selectedTags,
  onTagsSelected,
}: {
  selectedTags: string[];
  onTagsSelected: (tags: string[]) => void;
}) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");
  const debouncedSearchString = useDebounce(searchString, 500);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const tagsLoad = useLoad(
    async (signal) => {
      try {
        return await merchantRpcClient.searchCustomerTags(
          { searchString: debouncedSearchString },
          { signal },
        );
      } catch (error) {
        console.error("Error searching customer tags:", error);
        return { tags: [], total: 0 };
      }
    },
    [debouncedSearchString, merchantRpcClient],
  );

  const tagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    const loadedTags = tagsLoad.value?.tags ?? [];

    const allTags = new Set([...loadedTags, ...selectedTags]);

    return Array.from(allTags).map((tag) => ({
      id: tag,
      value: tag,
      type: "badge",
      badge: { text: tag, color: "gray" },
    }));
  }, [tagsLoad.value, selectedTags]);

  const selectedTagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return selectedTags.map((tag) => ({
      id: tag,
      value: tag,
      type: "badge",
      badge: { text: tag, color: "gray" },
    }));
  }, [selectedTags]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      onTagsSelected(items.map((item) => item.value));
    },
  );

  return (
    <Flex w="full">
      <RedoTagsDropdownInput<string>
        description={
          selectedTags.length > 0
            ? t("{{count}} tags selected", { count: selectedTags.length })
            : undefined
        }
        loading={tagsLoad.pending}
        options={tagOptions}
        placeholder={t("Enter or select tags")}
        searchPlaceholder={t("Search for customer tags...")}
        searchString={searchString}
        selectedOptions={selectedTagOptions}
        setSearchString={setSearchString}
        setSelectedOptions={handleSelectionChange}
        size="xs"
        totalOptionsIncludingNotShown={tagsLoad.value?.total}
      />
    </Flex>
  );
});
